<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const total = computed(() => props.metrics.total || 0);
const delivered = computed(() => props.metrics.delivered || 0);
const read = computed(() => props.metrics.read || 0);
const failed = computed(() => props.metrics.failed || 0);
const clicked = computed(() => props.metrics.clicked || 0);
const replied = computed(() => props.metrics.replied || 0);
const converted = computed(() => props.metrics.converted || 0);

const deliveryRate = computed(() => {
  if (total.value === 0) return 0;
  return Math.round((delivered.value / total.value) * 100);
});

const readRate = computed(() => {
  if (delivered.value === 0) return 0;
  return Math.round((read.value / delivered.value) * 100);
});

const failRate = computed(() => {
  if (total.value === 0) return 0;
  return Math.round((failed.value / total.value) * 100);
});

const clickRate = computed(() => {
  if (delivered.value === 0) return 0;
  return Math.round((clicked.value / delivered.value) * 100);
});

const replyRate = computed(() => {
  if (delivered.value === 0) return 0;
  return Math.round((replied.value / delivered.value) * 100);
});

const conversionRate = computed(() => {
  if (delivered.value === 0) return 0;
  return Math.round((converted.value / delivered.value) * 100);
});

const metricCards = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.TOTAL'),
    value: total.value,
    icon: 'i-lucide-send',
    color: 'text-n-blue-11',
    bgColor: 'bg-n-blue-3',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.DELIVERED'),
    value: delivered.value,
    rate: `${deliveryRate.value}%`,
    icon: 'i-lucide-check-check',
    color: 'text-n-teal-11',
    bgColor: 'bg-n-teal-3',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.READ'),
    value: read.value,
    rate: `${readRate.value}%`,
    icon: 'i-lucide-eye',
    color: 'text-n-violet-11',
    bgColor: 'bg-n-violet-3',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.CLICKED'),
    value: clicked.value,
    rate: `${clickRate.value}%`,
    icon: 'i-lucide-mouse-pointer-click',
    color: 'text-n-amber-11',
    bgColor: 'bg-n-amber-3',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.REPLIED'),
    value: replied.value,
    rate: `${replyRate.value}%`,
    icon: 'i-lucide-message-circle',
    color: 'text-n-green-11',
    bgColor: 'bg-n-green-3',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.CONVERTED'),
    value: converted.value,
    rate: `${conversionRate.value}%`,
    icon: 'i-lucide-target',
    color: 'text-n-jade-11',
    bgColor: 'bg-n-jade-3',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.METRICS.FAILED'),
    value: failed.value,
    rate: `${failRate.value}%`,
    icon: 'i-lucide-alert-circle',
    color: 'text-n-ruby-11',
    bgColor: 'bg-n-ruby-3',
  },
]);
</script>

<template>
  <div>
    <h4 class="mb-3 text-sm font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.DELIVERY_FUNNEL') }}
    </h4>
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <div
        v-for="card in metricCards"
        :key="card.label"
        class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak dark:border-n-strong bg-white dark:bg-n-solid-3"
      >
        <div class="flex items-center gap-2">
          <div
            class="flex items-center justify-center w-8 h-8 rounded-lg"
            :class="card.bgColor"
          >
            <span class="text-base" :class="[card.icon, card.color]" />
          </div>
          <span class="text-xs font-medium text-n-slate-11">
            {{ card.label }}
          </span>
        </div>
        <div class="flex items-baseline gap-2">
          <span class="text-2xl font-semibold text-n-slate-12">
            {{ card.value }}
          </span>
          <span v-if="card.rate" class="text-xs font-medium text-n-slate-10">
            {{ card.rate }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
